<template>
  <FormLayout
    :has-changes="hasChanges"
    :is-saving="isUpdatingLocationTags"
    title="Tags"
    @on-dismiss="handleDismiss"
    @on-save="handleSave"
  >
    <TagsEditor ref="tagsEditor" :initial-tags="location.tags" @error="handleError" />
    <template #snack-bar-wrapper>
      <vs-snackbar v-model="isSnackbarVisible" :type="snackbarType" sticky>
        {{ snackbarMessage ?? '' }}
      </vs-snackbar>
    </template>
  </FormLayout>
</template>

<script setup lang="ts">
import TagsEditor from '@/components/common/tags/TagsEditor.vue'
import { computed, ref, toRef } from 'vue'
import {
  BulkUpdateLocationsTagsOperation,
  useBulkUpdateLocationsTags,
  useLocationQuery,
} from '@/api/locations'
import FormLayout from '@/components/common/FormLayout.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import { SnackType } from '@/components/vision/SnackBarType'
import NewTag from '@/models/new_tag'

const props = defineProps<{
  locationId: string | number | null
}>()
const isSnackbarVisible = ref(false)
const snackbarMessage = ref()
const snackbarType = ref(SnackType.ERROR)

const emit = defineEmits(['dismiss'])
const locationIdRef = toRef(props, 'locationId')
const { data: location } = useLocationQuery(locationIdRef)
const { mutateAsync: updateLocationTags, isLoading: isUpdatingLocationTags } =
  useBulkUpdateLocationsTags()

const tagsEditor = ref()
const hasChanges = computed(() => tagsEditor.value?.hasChanges ?? false)
const handleSave = async () => {
  if (!locationIdRef.value) return
  await updateLocationTags({
    locationIds: [Number(locationIdRef.value)],
    tags:
      tagsEditor.value?.currentTags
        .filter((tag) => tag.isEditable)
        .map((tag) => NewTag.toObject(tag).toDisplayString) ?? [],
    operation: BulkUpdateLocationsTagsOperation.OVERWRITE,
  })
  emit('dismiss')
}

const handleDismiss = () => {
  if (hasChanges.value) {
    const confirmation = window.confirm(
      'There are unsaved changes, are you sure you want to discard them?',
    )

    if (!confirmation) {
      return
    }
  }
  emit('dismiss')
}

const handleError = (err: any) => {
  snackbarMessage.value = err
  isSnackbarVisible.value = true
}
</script>
